"use client"

import { useState, useEffect, useCallback } from "react"
import { Container, Row, Col, Card, Nav, Tab, Table, Button, Form, Alert, Spinner, Badge } from "react-bootstrap"
import { toast } from "react-hot-toast"
import { apiRequest, endpoints } from "../utils/api"
import CropEditModal from "../components/CropEditModal"
import CropViewModal from "../components/CropViewModal"
import NotificationCenter from "../components/NotificationCenter"
import TemperatureMonitor from "../components/TemperatureMonitor"

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState("dashboard")
  const [loading, setLoading] = useState(false)
  const [analytics, setAnalytics] = useState(null)
  const [crops, setCrops] = useState([])
  const [settings, setSettings] = useState({})
  const [uploadFile, setUploadFile] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedCrop, setSelectedCrop] = useState(null)
  const [monitoringLocation, setMonitoringLocation] = useState(null)
  const [locationLoading, setLocationLoading] = useState(false)

  // Get auth headers (stable with useCallback)
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("adminToken")
    return {
      Authorization: `Bearer ${token}`,
    }
  }, [])

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiRequest("/api/admin/analytics", {
        headers: getAuthHeaders(),
      })
      setAnalytics(response)
    } catch (error) {
      console.error("Analytics fetch error:", error)
      toast.error("Failed to fetch analytics")
      setAnalytics({
        totalSearches: 0,
        totalCrops: 0,
        popularLocations: [],
        recentSearches: [],
      })
    } finally {
      setLoading(false)
    }
  }, [getAuthHeaders])

  // Fetch crops — endpoints.crops is NOT added as dep (it's not a React dep)
  const fetchCrops = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiRequest(endpoints.crops, {
        headers: getAuthHeaders(),
      })
      setCrops(response)
    } catch (error) {
      toast.error("Failed to fetch crops")
    } finally {
      setLoading(false)
    }
  }, [getAuthHeaders]) // Removed `endpoints.crops` — it's static, not a reactive value

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiRequest("/api/admin/settings", {
        headers: getAuthHeaders(),
      })
      setSettings(response)
    } catch (error) {
      toast.error("Failed to update settings")
    } finally {
      setLoading(false)
    }
  }, [getAuthHeaders])

  // Fetch monitoring location
  const fetchMonitoringLocation = useCallback(async () => {
    try {
      setLocationLoading(true)
      const response = await apiRequest("/api/admin/monitoring-location", {
        headers: getAuthHeaders(),
      })
      setMonitoringLocation(response.location)
    } catch (error) {
      console.error("Failed to fetch monitoring location:", error)
      toast.error("Failed to fetch monitoring location")
    } finally {
      setLocationLoading(false)
    }
  }, [getAuthHeaders])

  // Effect that runs based on activeTab and calls respective fetch functions
  useEffect(() => {
    if (activeTab === "dashboard") {
      fetchAnalytics()
    } else if (activeTab === "crops") {
      fetchCrops()
    } else if (activeTab === "settings") {
      fetchSettings()
    } else if (activeTab === "monitoring") {
      fetchMonitoringLocation()
    }
  }, [
    activeTab,
    fetchAnalytics,
    fetchCrops,
    fetchSettings,
    fetchMonitoringLocation,
  ])

  // Other handlers remain same

  const handleFileUpload = async (e) => {
    e.preventDefault()

    if (!uploadFile) {
      toast.error("Please select a file to upload")
      return
    }

    const formData = new FormData()
    formData.append("cropFile", uploadFile)

    try {
      setLoading(true)
      const token = localStorage.getItem("adminToken")
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8787"}/api/admin/crops/upload`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      )

      if (!response.ok) {
        throw new Error("Upload failed")
      }

      const result = await response.json()
      toast.success(`Upload successful! ${result.results.inserted} crops added, ${result.results.updated} updated`)
      setUploadFile(null)
      fetchCrops() // Refresh crops list
    } catch (error) {
      toast.error(error.message || "Upload failed")
    } finally {
      setLoading(false)
    }
  }

  const handleSettingsUpdate = async (e) => {
    e.preventDefault()

    try {
      setLoading(true)
      await apiRequest("/api/admin/settings", {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(settings),
      })
      toast.success("Settings updated successfully")
    } catch (error) {
      toast.error("Failed to update settings")
    } finally {
      setLoading(false)
    }
  }

  const deleteCrop = async (cropId) => {
    if (!window.confirm("Are you sure you want to delete this crop?")) {
      return
    }

    try {
      await apiRequest(`${endpoints.crops}/${cropId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      })
      toast.success("Crop deleted successfully")
      fetchCrops() // Refresh crops list
    } catch (error) {
      toast.error("Failed to delete crop")
    }
  }

  const saveMonitoringLocation = async () => {
    if (!monitoringLocation?.lat || !monitoringLocation?.lng) {
      toast.error("Please enter valid latitude and longitude")
      return
    }

    try {
      setLocationLoading(true)
      await apiRequest("/api/admin/monitoring-location", {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(monitoringLocation),
      })
      toast.success("Monitoring location saved successfully")
    } catch (error) {
      console.error("Failed to save monitoring location:", error)
      toast.error("Failed to save monitoring location")
    } finally {
      setLocationLoading(false)
    }
  }

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>🛠️ Admin Panel</h2>
            <NotificationCenter />
          </div>
        </Col>
      </Row>

      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Row>
          <Col md={3}>
            <Nav variant="pills" className="flex-column">
              <Nav.Item>
                <Nav.Link eventKey="dashboard">📊 Dashboard</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="crops">🌾 Crop Database</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="upload">📤 Upload Data</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="monitoring">🌡️ Monitoring</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="settings">⚙️ Settings</Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>

          <Col md={9}>
            <Tab.Content>
              {/* Dashboard Tab */}
              <Tab.Pane eventKey="dashboard">
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">📈 Analytics Dashboard</h5>
                  </Card.Header>
                  <Card.Body>
                    {loading ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                      </div>
                    ) : analytics ? (
                      <>
                        {/* Stats Cards */}
                        <Row className="mb-4">
                          <Col md={4}>
                            <Card className="bg-primary text-white">
                              <Card.Body className="text-center">
                                <h3>{analytics.totalSearches}</h3>
                                <p className="mb-0">Total Searches</p>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col md={4}>
                            <Card className="bg-success text-white">
                              <Card.Body className="text-center">
                                <h3>{analytics.totalCrops}</h3>
                                <p className="mb-0">Crops in Database</p>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col md={4}>
                            <Card className="bg-info text-white">
                              <Card.Body className="text-center">
                                <h3>{analytics.popularLocations?.length || 0}</h3>
                                <p className="mb-0">Popular Locations</p>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>

                        {/* Popular Locations */}
                        <Row>
                          <Col md={6}>
                            <Card>
                              <Card.Header>
                                <h6 className="mb-0">🗺️ Popular Locations</h6>
                              </Card.Header>
                              <Card.Body>
                                {analytics.popularLocations?.map((location, index) => (
                                  <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                                    <span>{location.location_name}</span>
                                    <Badge bg="secondary">{location.search_count} searches</Badge>
                                  </div>
                                ))}
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col md={6}>
                            <Card>
                              <Card.Header>
                                <h6 className="mb-0">🕒 Recent Searches</h6>
                              </Card.Header>
                              <Card.Body>
                                {analytics.recentSearches?.slice(0, 5).map((search, index) => (
                                  <div key={index} className="mb-2">
                                    <div className="fw-medium">{search.location_name}</div>
                                    <small className="text-muted">
                                      {new Date(search.last_searched).toLocaleDateString()}
                                    </small>
                                  </div>
                                ))}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </>
                    ) : (
                      <Alert variant="info">No analytics data available</Alert>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Crops Tab */}
              <Tab.Pane eventKey="crops">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">🌾 Crop Database Management</h5>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => {
                        setSelectedCrop(null)
                        setShowEditModal(true)
                      }}
                    >
                      <i className="fas fa-plus me-1"></i>
                      Add New Crop
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {loading ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table striped hover>
                          <thead>
                            <tr>
                              <th>Crop Name</th>
                              <th>Min Temp (°C)</th>
                              <th>Max Temp (°C)</th>
                              <th>Growth Period (days)</th>
                              <th>Soil Type</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {crops.map((crop) => (
                              <tr key={crop.id}>
                                <td className="fw-medium">{crop.name}</td>
                                <td>{crop.min_temp}°</td>
                                <td>{crop.max_temp}°</td>
                                <td>{crop.growth_period_days}</td>
                                <td>{crop.soil_type && <Badge bg="outline-secondary">{crop.soil_type}</Badge>}</td>
                                <td>
                                  <div className="btn-group" role="group">
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedCrop(crop)
                                        setShowViewModal(true)
                                      }}
                                      title="View Details"
                                    >
                                      <i className="fas fa-eye"></i>
                                    </Button>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedCrop(crop)
                                        setShowEditModal(true)
                                      }}
                                      title="Edit Crop"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => deleteCrop(crop.id)}
                                      title="Delete Crop"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>

                        {crops.length === 0 && (
                          <div className="text-center py-4 text-muted">
                            <i className="fas fa-seedling fa-3x mb-3"></i>
                            <h5>No crops in database</h5>
                            <p>Start by adding some crops to the database</p>
                          </div>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Upload Tab */}
              <Tab.Pane eventKey="upload">
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">📤 Upload Crop Data</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form onSubmit={handleFileUpload}>
                      <Form.Group className="mb-3">
                        <Form.Label>Select CSV or Excel File</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".csv,.xlsx,.xls"
                          onChange={(e) => setUploadFile(e.target.files[0])}
                          disabled={loading}
                        />
                        <Form.Text className="text-muted">Supported formats: CSV, Excel (.xlsx, .xls)</Form.Text>
                      </Form.Group>

                      <Button type="submit" variant="success" disabled={loading || !uploadFile}>
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Uploading...
                          </>
                        ) : (
                          "Upload File"
                        )}
                      </Button>
                    </Form>

                    <Alert variant="info" className="mt-4">
                      <h6>Required CSV Columns:</h6>
                      <ul className="mb-0">
                        <li>
                          <code>name</code> - Crop name
                        </li>
                        <li>
                          <code>min_temp</code> - Minimum temperature (°C)
                        </li>
                        <li>
                          <code>max_temp</code> - Maximum temperature (°C)
                        </li>
                        <li>
                          <code>growth_period_days</code> - Growth period in days
                        </li>
                        <li>
                          <code>soil_type</code> - Soil type (optional)
                        </li>
                      </ul>
                    </Alert>
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Monitoring Tab */}
              <Tab.Pane eventKey="monitoring">
                <Row>
                  <Col>
                    <Card className="mb-4">
                      <Card.Header>
                        <h5 className="mb-0">🗺️ Set Monitoring Location</h5>
                      </Card.Header>
                      <Card.Body>
                        {locationLoading ? (
                          <div className="text-center py-3">
                            <Spinner animation="border" size="sm" />
                            <span className="ms-2">Loading location...</span>
                          </div>
                        ) : (
                          <>
                            <Form.Group className="mb-3">
                              <Form.Label>Location Coordinates</Form.Label>
                              <div className="d-flex gap-2">
                                <Form.Control
                                  type="number"
                                  placeholder="Latitude"
                                  step="0.0001"
                                  value={monitoringLocation?.lat || ""}
                                  onChange={(e) =>
                                    setMonitoringLocation((prev) => ({
                                      ...prev,
                                      lat: Number.parseFloat(e.target.value) || 0,
                                    }))
                                  }
                                />
                                <Form.Control
                                  type="number"
                                  placeholder="Longitude"
                                  step="0.0001"
                                  value={monitoringLocation?.lng || ""}
                                  onChange={(e) =>
                                    setMonitoringLocation((prev) => ({
                                      ...prev,
                                      lng: Number.parseFloat(e.target.value) || 0,
                                    }))
                                  }
                                />
                                <Form.Control
                                  type="text"
                                  placeholder="Location Name (optional)"
                                  value={monitoringLocation?.name || ""}
                                  onChange={(e) =>
                                    setMonitoringLocation((prev) => ({
                                      ...prev,
                                      name: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                              <Form.Text className="text-muted">
                                Set a specific location to monitor temperature conditions and receive alerts for crop
                                suitability
                              </Form.Text>
                            </Form.Group>

                            <div className="d-flex gap-2">
                              <Button
                                variant="primary"
                                onClick={saveMonitoringLocation}
                                disabled={locationLoading || !monitoringLocation?.lat || !monitoringLocation?.lng}
                              >
                                {locationLoading ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Saving...
                                  </>
                                ) : (
                                  "Save Location"
                                )}
                              </Button>

                              <Button
                                variant="outline-secondary"
                                onClick={() => setMonitoringLocation({ lat: 0, lng: 0, name: "" })}
                              >
                                Clear
                              </Button>
                            </div>

                            {monitoringLocation?.setAt && (
                              <Alert variant="success" className="mt-3">
                                <small>Location set on: {new Date(monitoringLocation.setAt).toLocaleString()}</small>
                              </Alert>
                            )}
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {monitoringLocation && monitoringLocation.lat && monitoringLocation.lng && (
                  <Row>
                    <Col>
                      <TemperatureMonitor location={monitoringLocation} crops={crops} />
                    </Col>
                  </Row>
                )}
              </Tab.Pane>

              {/* Settings Tab */}
              <Tab.Pane eventKey="settings">
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">⚙️ Application Settings</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form onSubmit={handleSettingsUpdate}>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Weather API Provider</Form.Label>
                            <Form.Select
                              value={settings.weather_api_provider || "open-meteo"}
                              onChange={(e) => setSettings({ ...settings, weather_api_provider: e.target.value })}
                            >
                              <option value="open-meteo">Open-Meteo (Free)</option>
                              <option value="meteostat">Meteostat (API Key Required)</option>
                              <option value="visual-crossing">Visual Crossing (Paid)</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Default Year Range</Form.Label>
                            <Form.Select
                              value={settings.default_year_range || "5"}
                              onChange={(e) => setSettings({ ...settings, default_year_range: e.target.value })}
                            >
                              <option value="1">1 Year</option>
                              <option value="2">2 Years</option>
                              <option value="5">5 Years</option>
                              <option value="7">7 Years</option>
                              <option value="10">10 Years</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Button type="submit" variant="primary" disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving...
                          </>
                        ) : (
                          "Save Settings"
                        )}
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>

      {/* Crop Edit Modal */}
      <CropEditModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false)
          setSelectedCrop(null)
        }}
        crop={selectedCrop}
        onSave={fetchCrops}
      />

      {/* Crop View Modal */}
      <CropViewModal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false)
          setSelectedCrop(null)
        }}
        crop={selectedCrop}
      />
    </Container>
  )
}

export default AdminPage